import { License } from './license.model';

export enum UserState {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  DELETED = 'DELETED'
}

export enum ExperiencePermissions {
  FULL_ACCESS = 'FullAccess'
}

export enum WorkroomPrivilege {
  CURATOR = 'Curating',
  PARTICIPANT = 'Participating'
}

export enum ExperiencePrivilege {
  FULL_ACCESS = 'FullAccess',
  NONE = 'None'
}

/** @deprecated Use 'AccountUserRole' instead. **/
export enum AccountMemberRoleLegacy {
  MEMBER = 'MEMBER',
  MANAGER = 'MANAGER'
}

export enum AccountMemberRole {
  API = 'API',
  ACCOUNT_ADMIN = 'ACCOUNT_ADMIN',
  CURATOR = 'CURATOR',
  PARTICIPANT = 'PARTICIPANT',
  CONSUMER = 'CONSUMER'
}

export enum InvitationStatus {
  /** For email based invitation. The user has to accept the invitation to become a member. */
  INVITED = 'INVITED',
  /** The user accepted a personal (email based) invitation. */
  ACCEPTED = 'ACCEPTED',
  /** The user rejected a personal (email based) invitation. */
  REJECTED = 'REJECTED',
  /** For shared link based invitation requests. One of the account managers has to approve the invitation for the user to become a member. */
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  /** An account manager approved the link based invitation request. */
  APPROVED = 'APPROVED',
  /** An account manager rejected the link based invitation request. */
  DISAPPROVED = 'DISAPPROVED'
}

export enum AccountRequestStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  DISAPPROVED = 'DISAPPROVED'
}

export enum AccountAccessStatus {
  /** Account/User has been initialized and active. */
  ACTIVE = 'ACTIVE',
  /** Account/User has been disabled. */
  INACTIVE = 'INACTIVE'
}

export interface CHRepository {
  repositoryId: string;
  url: string;
}

export interface UserInvitation {
  id: string;
  accountId: string;
  invitationStatus: InvitationStatus;
  lastInvitationStatusChange: Date;
  inviterFirstName: string;
  inviterLastName: string;
  inviterEmail: string;
  reason: string;
  accountName: string;
  accountLogoDownloadLink: string;
  accountAccessToken: string;
}

export interface TrialPlan {
  accountRequestId: string;
  accountRequestOrgName: string;
  status: AccountRequestStatus;
}

/**
 * Branding which an account admin configures in SACC ("Corporate branding") and which Home applies on its dashboard.
 * SACC always writes a `titleColor`, everything else is optional and sent as `null` if it is not configured.
 */
export interface HomeBranding {
  titleColor: string;
  /** Download link for the logo, requires the `accountAccessToken` to be appended. */
  logoUrl?: string;
  /** SACC always keeps a color, but a configured `backgroundUrl` takes precedence over it. */
  backgroundColor?: string;
  /** Download link for the background image, requires the `accountAccessToken` to be appended. */
  backgroundUrl?: string;
}

/** Only contains the branding themes which are currently published. */
export interface AccountBranding {
  home?: HomeBranding;
}

export interface AccountAccess {
  accountId: string;
  accountName: string;
  status: AccountAccessStatus;
  accountAccessToken: string;
  licenses: License[];
  ownerOid: string;
  ownerName: string;
  ownerEmail: string;
  profilePictureDownloadLink: string;
  storageLimit: number;
  /** @deprecated Use 'effectiveRole' instead **/
  role: AccountMemberRoleLegacy;
  accountLogoDownloadLink: string;
  ownerPhoneNumber?: string;
  discriminator: 'account-access';
  /** @deprecated Will be replaced by 'effectiveRole' **/
  privileges: {
    work: WorkroomPrivilege | null;
    experience: ExperiencePrivilege | null;
  };
  // New role property
  effectiveRole?: AccountMemberRole;
  branding?: AccountBranding;
}

export enum ProvisioningType {
  AUTOMATIC = 'AUTOMATIC',
  MANUAL = 'MANUAL'
}

export interface UserDetails {
  oid: string;
  firstName: string;
  lastName: string;
  email: string;
  /** @deprecated Use cookie-based authorization instead **/
  userAccessToken: string;
  accountAccesses: AccountAccess[];
  profilePictureDownloadLink: string;
  status: UserState;
  locale: string;
  admin: boolean;
  invitations: UserInvitation[];
  trialPlan: TrialPlan;
  provisioningType: ProvisioningType;
  displayName?: string;
}
